import clsx from 'clsx';
import { AlertTriangle, Inbox, Loader2, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from './Form';

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('size-4 animate-spin text-zinc-400', className)} />;
}

export function LoadingBlock({ label = 'Загрузка…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-400">
      <Spinner />
      {label}
    </div>
  );
}

export function EmptyState({
  title = 'Нет данных',
  description,
  icon,
  action,
}: {
  title?: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      <div className="text-zinc-600">{icon ?? <Inbox className="size-7" />}</div>
      <div className="text-sm font-medium text-zinc-300">{title}</div>
      {description ? <div className="max-w-md text-xs text-zinc-500">{description}</div> : null}
      {action}
    </div>
  );
}

export function ErrorBanner({
  message,
  onRetry,
  className,
  title = 'Ошибка',
}: {
  message: ReactNode;
  onRetry?: () => void;
  className?: string;
  title?: string;
}) {
  return (
    <div
      className={clsx(
        'flex flex-col gap-2 rounded-lg border border-red-900/60 bg-red-950/40 p-3 text-sm text-red-200 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-400" />
        <div className="min-w-0">
          <div className="font-medium">{title}</div>
          <div className="break-words text-xs text-red-300/90">{message}</div>
        </div>
      </div>
      {onRetry ? (
        <Button size="sm" variant="secondary" icon={<RefreshCw className="size-3.5" />} onClick={onRetry}>
          Повторить
        </Button>
      ) : null}
    </div>
  );
}

export function WarningBanner({ message, title }: { message: ReactNode; title?: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-900/60 bg-amber-950/40 p-3 text-sm text-amber-200">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-400" />
      <div className="min-w-0">
        {title ? <div className="font-medium">{title}</div> : null}
        <div className="break-words text-xs text-amber-300/90">{message}</div>
      </div>
    </div>
  );
}
